<template>
  <div>
    <v-layout
      v-if="isFormValid === false"
      mb-0
      mt-3
    >
      <v-flex>
        <div class="v-message text-xs-center">
          <div class="v-messages__message">
            {{ $t('errors.form_not_valid') }}
          </div>
        </div>
      </v-flex>
    </v-layout>
    <SaveDraftButton
      v-if="showSaveDraftButton"
      :wio-struct-id="wioStructId"
      :warehouse-type="warehouseType"
      @clearFlags="clearValidatorFlags"
    />
    <v-layout
      justify-space-between
      align-center
      class="mb-4"
      :column="$vuetify.breakpoint.smAndDown"
    >
      <v-flex class="none-growth">
        <a
          v-if="editMode"
          class="link_iconed"
          @click="moveToCard()"
        >
          <v-icon>keyboard_arrow_left</v-icon> {{ $t('warehouses.buttons.back_to_card') }}
        </a>
        <a
          v-else
          class="link_iconed"
          @click="moveToList()"
        >
          <v-icon>keyboard_arrow_left</v-icon> {{ $t('warehouses.buttons.back') }}
        </a>
      </v-flex>
      <v-flex class="none-growth">
        <v-btn
          v-if="buttonVisible"
          color="sz_violet"
          class="mr-0 text-xs-center"
          :loading="loader"
          @click="save()"
        >
          {{ saveButtonTitle }}
        </v-btn>
      </v-flex>
    </v-layout>
    <InfoModal
      :text="$t('warehouses.add_rejon_warehouse.info')"
      :visible="showInfoModal"
      @close="showInfoModal = false"
    />
  </div>
</template>

<script>
import SaveDraftButton from '@/components/warehouse/SaveDraftButton';
import fieldRemover from '@/utils/fieldsRemover';
import InfoModal from '@/components/widgets/InfoModal';
import filterArray from '@/mixins/filterArray';

import isEmpty from 'lodash.isempty';
import warehousesAcceptPermissions from '@/mixins/warehousesAcceptPermissions';
import scrollToTopErrorMessage from '@/mixins/scrollToTopErrorMessage';
import formatDecimalVariables from '@/mixins/formatDecimalVariables';

import WAREHOUSE_TYPE from '@/constants/warehouse/warehouseType';

export default {
    name: 'EndButtons',
    components: { SaveDraftButton, InfoModal },
    mixins: [
        warehousesAcceptPermissions,
        filterArray,
        scrollToTopErrorMessage,
        formatDecimalVariables
    ],
    inject: ['$validator'],
    props: {
        warehouseType: {
            required: true,
            type: Number
        },
        rejonHasWarehouse: {
            type: Boolean,
            default: false
        },
        readOnly: {
            type: Boolean,
            default: false
        },
        editMode: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            loader: false,
            showInfoModal: false,
            isFormValid: true,
            WAREHOUSE_TYPE
        };
    },
    computed: {
        wioStructId () {
            return this.warehouseType === this.WAREHOUSE_TYPE.REGIONAL
                ? this.$store.getters['warehouses/getSelectedRejon'].id
                : this.$store.getters['warehouses/getSelectedProvince'].province_id;
        },
        warehouseForVerifinig () {
            if (this.warehouseType === this.WAREHOUSE_TYPE.REGIONAL) {
                return this.rejonHasWarehouse && this.canEdit;
            } else {
                return this.readOnly;
            }
        },
        saveButtonTitle () {
            if (this.warehouseForVerifinig) {
                return this.$t('buttons.approve_and_publish');
            }
            if (this.canEditPublishedWarehouse) {
                return this.$t('buttons.save_changes');
            }
            return this.$t('warehouses.buttons.add_warehouse');
        },
        warehouseId () {
            if (Object.prototype.hasOwnProperty.call(this.$route.params, 'warehouseId')) {
                return this.$route.params.warehouseId;
            }
            return false;
        },
        canSaveSupportingWarehouseDraft () {
            return this.$store.getters['warehouses/getShowDraftButton'];
        },
        showSaveDraftButton () {
            if (this.warehouseType === this.WAREHOUSE_TYPE.REGIONAL) {
                return !this.rejonHasWarehouse;
            } else {
                return !this.readOnly && this.canSaveSupportingWarehouseDraft;
            }
        },
        buttonVisible () {
            return !this.rejonHasWarehouse || this.warehouseForVerifinig || this.canEditPublishedWarehouse;
        }
    },
    methods: {
        moveToList () {
            this.$router.push({ name: 'warehouse-list' });
        },
        moveToCard () {
            this.$router.go(-1);
        },
        clearValidatorFlags () {
            Object.keys(this.$validator.flags).forEach((x) => {
                this.$validator.flags[x].changed = false;
            });
        },
        createWarehouse (_data) {
            return this.$store.dispatch('warehouses/createWarehouse', _data).then(() => {
                this.removeWarehouseDraft();
                this.redirectWarehousesList();
            });
        },
        editWarehouse (_data) {
            const data = {
                id: this.warehouseId,
                data: _data
            };
            return this.$store.dispatch('warehouses/updateWarehouse', data);
        },
        acceptWarehouse () {
            return this.$store.dispatch('warehouse/acceptWarehouse', this.warehouseId);
        },
        redirectWarehousesList () {
            this.loader = false;
            this.clearValidatorFlags();
            this.$router.push({ name: 'warehouse-list' });
        },
        removeWarehouseDraft () {
            if (!isEmpty(this.$store.getters['warehouses/getWarehouseDraft'])) {
                this.$store.dispatch('warehouses/removeWarehouseDraft', this.wioStructId);
            }
        },
        validateData () {
            this.$validator.validateAll().then((valid) => {
                this.isFormValid = valid;
                if (valid) {
                    let _data;
                    if (this.warehouseType === this.WAREHOUSE_TYPE.REGIONAL) {
                        const _warehouseData = this.$store.getters['warehouses/getAddWarehouseData'];
                        _data = {
                            warehouse_type: 'regional',
                            warehouse_name: _warehouseData.locationData.warehouseName,
                            location_name: _warehouseData.locationData.address.locationName,
                            street: _warehouseData.locationData.address.street,
                            building_number: _warehouseData.locationData.address.placeNumber,
                            flat_number: _warehouseData.locationData.address.flatNumber,
                            city: _warehouseData.locationData.address.city,
                            province: _warehouseData.locationData.address.province,
                            wio_struct_location_id: _warehouseData.locationData.address.rejon,
                            postal_code: _warehouseData.locationData.address.postalCode,
                            warehouse_manager: _warehouseData.warehouseManager.manager,
                            warehouse_data: {
                                base_info: {
                                    ..._warehouseData.baseInfo
                                },
                                location_data: {
                                    opening_hours: {
                                        ..._warehouseData.locationData.openingHours
                                    },
                                    directions: _warehouseData.locationData.directions
                                },
                                additional_parameters: {
                                    ..._warehouseData.additionalParameters
                                },
                                warehouse_manager: {
                                    candidate: _warehouseData.warehouseManager.candidate,
                                    phone: _warehouseData.warehouseManager.phone
                                },
                                additional_note: _warehouseData.additionalNote
                            }
                        };

                        fieldRemover(
                            _data.warehouse_data,
                            'base_info',
                            _warehouseData.baseInfo.showOptionalQuestion !== true,
                            'wayForWarehouseOperation'
                        );
                        fieldRemover(
                            _data.warehouse_data,
                            'base_info',
                            _warehouseData.baseInfo.showOptionalQuestion !== true ||
                _warehouseData.baseInfo.showOptionalQuestion === true,
                            'showOptionalQuestion'
                        );
                        fieldRemover(
                            _data.warehouse_data.location_data,
                            'opening_hours',
                            _warehouseData.locationData.openingHours.additionalFirstDayChecked !== 'yes',
                            'firstDayFrom firstDayTo'
                        );
                        fieldRemover(
                            _data.warehouse_data.location_data,
                            'opening_hours',
                            _warehouseData.locationData.openingHours.additionalThirdDayChecked !== 'yes',
                            'thirdDayFrom thirdDayTo'
                        );
                        fieldRemover(
                            _data.warehouse_data,
                            'additional_parameters',
                            _warehouseData.additionalParameters.warehousePotential !== 'yes',
                            'warehouseEquipment'
                        );
                    }
                    if (this.warehouseType === this.WAREHOUSE_TYPE.SUPPORTING) {
                        const _warehouseData = this.$store.getters['warehouses/getAddSupportingWarehouseData'];
                        _data = {
                            warehouse_type: 'supporting',
                            warehouse_name: _warehouseData.locationData.warehouseName,
                            location_name: _warehouseData.locationData.address.locationName,
                            street: _warehouseData.locationData.address.street,
                            building_number: _warehouseData.locationData.address.placeNumber,
                            flat_number: _warehouseData.locationData.address.flatNumber,
                            city: _warehouseData.locationData.address.city,
                            province: _warehouseData.locationData.address.province,
                            wio_struct_location_id: _warehouseData.locationData.address.province,
                            postal_code: _warehouseData.locationData.address.postalCode,
                            warehouse_manager: _warehouseData.warehouseManager.manager,
                            warehouse_data: {
                                base_info: {
                                    ..._warehouseData.baseInfo
                                },
                                location_data: {
                                    opening_hours: {
                                        ..._warehouseData.locationData.openingHours
                                    },
                                    directions: _warehouseData.locationData.directions
                                },
                                additional_parameters: {
                                    ..._warehouseData.additionalParameters
                                },
                                warehouse_manager: {
                                    phone: _warehouseData.warehouseManager.phone
                                },
                                additional_note: _warehouseData.additionalNote
                            }
                        };

                        fieldRemover(
                            _data.warehouse_data,
                            'base_info',
                            _warehouseData.baseInfo.showWayForWarehouseOperation !== true,
                            'wayForWarehouseOperation'
                        );
                        fieldRemover(
                            _data.warehouse_data,
                            'base_info',
                            _warehouseData.baseInfo.showWayForWarehouseOperation !== true ||
                _warehouseData.baseInfo.showWayForWarehouseOperation === true,
                            'showWayForWarehouseOperation'
                        );
                        fieldRemover(
                            _data.warehouse_data,
                            'base_info',
                            _warehouseData.baseInfo.supportingWarehouseType !== 334,
                            'isLocationFree'
                        );
                        fieldRemover(
                            _data.warehouse_data,
                            'base_info',
                            _warehouseData.baseInfo.supportingWarehouseType !== 334 ||
                _warehouseData.baseInfo.isLocationFree !== 'no',
                            'whyThisLocation'
                        );
                        fieldRemover(
                            _data.warehouse_data,
                            'base_info',
                            _warehouseData.baseInfo.supportingWarehouseType !== 334 ||
                _warehouseData.baseInfo.isLocationFree !== 'no' ||
                (_warehouseData.baseInfo.barter !== '' && _warehouseData.baseInfo.cost === ''),
                            'cost'
                        );
                        fieldRemover(
                            _data.warehouse_data,
                            'base_info',
                            _warehouseData.baseInfo.supportingWarehouseType !== 334 ||
                _warehouseData.baseInfo.isLocationFree !== 'no' ||
                (_warehouseData.baseInfo.cost !== '' && _warehouseData.baseInfo.barter === ''),
                            'barter'
                        );
                        fieldRemover(
                            _data.warehouse_data,
                            'location_data',
                            _warehouseData.locationData.directions === null ||
                _warehouseData.locationData.directions === '',
                            'directions'
                        );
                        fieldRemover(
                            _data.warehouse_data,
                            'additional_parameters',
                            _warehouseData.additionalParameters.showWarehouseOperation !== true,
                            'warehouseOperation'
                        );
                        fieldRemover(
                            _data.warehouse_data,
                            'additional_parameters',
                            _warehouseData.additionalParameters.showWarehouseOperation !== true ||
                _warehouseData.additionalParameters.showWarehouseOperation === true,
                            'showWarehouseOperation'
                        );
                    }
                    _data.street = _data.street === false ? null : _data.street;
                    _data.warehouse_data.base_info.baseParameters = this.filterArray(
                        _data.warehouse_data.base_info.baseParameters
                    );
                    _data.warehouse_data.additional_parameters.size = this.formatDecimalVariable(
                        _data.warehouse_data.additional_parameters.size
                    );
                    if (
                        _data?.warehouse_data?.base_info &&
            Object.prototype.hasOwnProperty.call(_data.warehouse_data.base_info, 'cost')
                    ) {
                        _data.warehouse_data.base_info.cost = this.formatDecimalVariable(
                            _data.warehouse_data.base_info.cost
                        );
                    }
                    if (this.canEditPublishedWarehouse) {
                        this.editWarehouse(_data)
                            .then(() => {
                                this.clearValidatorFlags();
                                this.$store.commit('warehouses/setWarehouseWasEdited', true);
                                this.$router.push({ name: 'warehouse-card', params: { id: this.warehouseId } });
                            })
                            .finally(() => {
                                this.loader = false;
                            });
                    } else if (!this.warehouseForVerifinig) {
                        this.createWarehouse(_data);
                    } else if (this.warehouseForVerifinig) {
                        this.editWarehouse(_data)
                            .then(() => {
                                this.acceptWarehouse().then(() => {
                                    this.clearValidatorFlags();
                                    this.$store.commit('warehouses/setWarehouseWasEdited', true);
                                    this.$router.push({ name: 'warehouse-card', params: { id: this.warehouseId } });
                                });
                            })
                            .finally(() => {
                                this.loader = false;
                            });
                    }
                } else {
                    this.scrollToTopErrorMessage();
                    this.loader = false;
                }
            });
        },
        save () {
            this.loader = true;
            if (
                this.warehouseType === 1 &&
        !this.warehouseForVerifinig &&
        !this.canEditPublishedWarehouse
            ) {
                this.$store.dispatch('warehouses/existRejonWarehouse', this.wioStructId).then(() => {
                    if (this.$store.getters['warehouses/getRejonHasWarehouse']) {
                        this.showInfoModal = true;
                    } else {
                        this.validateData();
                    }
                });
            } else {
                this.validateData();
            }
        }
    }
};
</script>

<style scoped></style>
